import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import type { StepsDatabase } from '../stepsDatabase';
import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from '../analytics';

import { noStepsCommand, tooManyRetriesCommand } from './commands';
import type { RestoreOptions } from './restore-steps';
import { createLockCallback } from './restore-steps';

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

/**
 * Validates if there are steps that need to be restored from offline storage.
 *
 * Returns true if there are steps in the StepsDatabase.
 *
 * Returns false if:
 * - The database doesn't exist, OR
 * - IndexedDB is not available, OR
 * - There are no steps in the StepsDatabase
 *
 * Throws any errors encountered during validation to trigger the retry mechanism.
 */
export async function isStepsRestoreRequired(
	stepsDatabase: StepsDatabase,
	pmSchema: PMSchema,
): Promise<boolean> {
	// If there's an error, the validateIndexedDBAccess will disable the plugin.
	const isIndexedDBAvailable = await stepsDatabase.validateIndexedDBAccess();
	if (!isIndexedDBAvailable) {
		return false;
	}

	const databases = await window.indexedDB.databases();
	const dbExists = Boolean(databases.find((db) => db.name === stepsDatabase.dbName));
	if (!dbExists) {
		return false;
	}

	// If there are steps, we need to restore them.
	const { steps } = await stepsDatabase.getData(pmSchema);
	if (steps.length > 0) {
		return true;
	}

	return false;
}

/**
 * When the restoreStepsAction fails, we give it a few retries to restore the steps with a delay between each attempt.
 * Returns a retry function if retry should happen, or null if max retries exceeded.
 */
export async function handleFailedRestoreStepsAction(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	editorView: EditorView,
	pmSchema: PMSchema,
	retryCount: number,
	error: unknown,
): Promise<void> {
	if (retryCount >= MAX_RETRIES) {
		api?.core.actions.execute(tooManyRetriesCommand());

		fireOfflineStorageAnalyticsEvent({
			api,
			action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR,
			errorMessage: `Exceeded max retries (${MAX_RETRIES}) with delay of ${RETRY_DELAY}ms`,
			errorInfo: parseErrorMessage(error),
			maxRetries: MAX_RETRIES,
			retrySource: 'isStepsRestoreRequired',
		});
		return;
	}

	await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
	void restoreStepsAction(api, stepsDatabase, editorView, retryCount + 1)(pmSchema);
}

/**
 * The main function that restores the steps from the IndexedDB database when initialising the page.
 * The leader path is responsible for acquiring the sync lock and restoring the steps. Any other tabs will
 * become followers and wait for the leader to restore the steps first.
 */
export function restoreStepsAction(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	editorView: EditorView,
	retryCount = 0,
	options?: RestoreOptions,
) {
	return async (pmSchema: PMSchema) => {
		try {
			const shouldRestore = await isStepsRestoreRequired(stepsDatabase, pmSchema);

			if (!shouldRestore) {
				api?.core.actions.execute(noStepsCommand());
				return;
			}
		} catch (error) {
			await handleFailedRestoreStepsAction(
				api,
				stepsDatabase,
				editorView,
				pmSchema,
				retryCount,
				error,
			);
			return;
		}

		fireOfflineStorageAnalyticsEvent({
			api,
			action: ACTION.OFFLINE_STORAGE_RESTORE_STEPS_INITIATED,
			retryCount,
		});

		const lockCallback = createLockCallback(api, stepsDatabase, pmSchema, options);
		void stepsDatabase.acquireSyncLock(lockCallback, false, 'restore');
	};
}
